import React from "react";
import { Gauge } from "lucide-react";
import { Panel } from "./Panel";
import { TermExplainer } from "./TermExplainer";

const REGIME_STYLES = {
  "RISK-ON": { text: "text-emerald-400", fill: "#10B981", badge: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  "NEUTRAL": { text: "text-amber-400", fill: "#F59E0B", badge: "bg-amber-500/10 text-amber-400 border-amber-500/30" },
  "RISK-OFF": { text: "text-rose-400", fill: "#F43F5E", badge: "bg-rose-500/10 text-rose-400 border-rose-500/30" },
};

// Semi-circular confidence gauge (SVG arc).
const Gauge180 = ({ value, color }) => {
  const r = 70;
  const cx = 90, cy = 84;
  const circ = Math.PI * r;
  const pct = Math.max(0, Math.min(100, value)) / 100;
  const angle = Math.PI * pct;
  const nx = cx - r * Math.cos(angle);
  const ny = cy - r * Math.sin(angle);
  return (
    <svg viewBox="0 0 180 100" className="w-full">
      <path d={`M20,84 A${r},${r} 0 0 1 160,84`} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="9" strokeLinecap="round" />
      <path
        d={`M20,84 A${r},${r} 0 0 1 160,84`}
        fill="none" stroke={color} strokeWidth="9" strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
        style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1)" }}
      />
      <line x1={cx} y1={cy} x2={nx} y2={ny} stroke="#fff" strokeWidth="2" />
      <circle cx={cx} cy={cy} r="4" fill="#fff" />
      <text x="20" y="98" fill="#52525B" fontSize="9" fontFamily="JetBrains Mono">0</text>
      <text x="152" y="98" fill="#52525B" fontSize="9" fontFamily="JetBrains Mono">100</text>
    </svg>
  );
};

export const MarketRegime = ({ regime, glossary }) => {
  const s = REGIME_STYLES[regime.state] || REGIME_STYLES["NEUTRAL"];
  return (
    <Panel data-testid="market-regime-card" topEdge className="flex h-full flex-col bg-gradient-to-b from-[#13161C] to-[#0C0E11] p-5">
      <div className="flex items-center justify-between">
        <div className="kicker flex items-center gap-1.5">
          <Gauge className="h-3.5 w-3.5 text-amber-400" /> India Market Regime
          <TermExplainer term={glossary.regime.term} what={glossary.regime.what} why={glossary.regime.why} />
        </div>
      </div>

      <div className="mt-3">
        <span data-testid="market-regime-status-badge" className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[11px] font-semibold tracking-wider ${s.badge}`}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.fill }} />
          {regime.state}
        </span>
        <div className={`mt-3 text-3xl font-bold tracking-tight ${s.text}`}>{regime.state}</div>
        <p className="mt-1 text-sm font-medium text-zinc-300">{regime.headline}</p>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between">
          <span className="kicker">Confidence</span>
          <span data-testid="market-regime-confidence-value" className="font-mono text-xl font-bold text-white tabular-nums">{regime.confidence}%</span>
        </div>
        <Gauge180 value={regime.confidence} color={s.fill} />
      </div>

      <p className="mt-2 text-xs leading-relaxed text-zinc-400">{regime.explanation}</p>

      <div data-testid="market-regime-why-it-matters" className="mt-4 rounded-md border border-amber-500/20 bg-amber-500/[0.06] p-3.5">
        <p className="kicker mb-1 text-amber-500/80">Why this matters</p>
        <p className="text-xs leading-relaxed text-amber-100/85">{regime.why_it_matters}</p>
      </div>
    </Panel>
  );
};
